import _set from 'lodash/set';
import _get from 'lodash/get';
import {getPageCountryCode, getPageLanguageCode} from '@malt/dom';
import {requireMessage} from '@malt/i18n';
import {defaultCitiesSuggestions, LocationAutocomplete} from '@malt/location-autocomplete';
import {LOCATION_CHANGED} from '../../../events/autocomplete';

// temporary fix, the line below doesn't work in production builds
Hop.requireMessage('search.location.remote.*');
requireMessage('search.location.remote.*');

export class GeocodeEarth {
    constructor(
        root,
        {
            id,
            inputName,
            defaultValue,
            allowRemote = false,
            allowRemoteEuropa = false,
            remoteIfEmpty = false,
            // other address autocompletes for which this module should be synchronized with
            synchronizedModuleIds = [],
            displaySuggestions = false,
        },
    ) {
        this.allowRemote = allowRemote;
        this.allowRemoteEuropa = allowRemoteEuropa;
        this.form = root;
        this.id = id;
        this.countryCode = getPageCountryCode();
        this.defaultValue = defaultValue;
        this.languageCode = getPageLanguageCode();
        this.queryInput = inputName
            ? this.form.querySelector(`input[name=${inputName}]`)
            : this.form.querySelector('input[name=location]') || this.form.querySelector('input[name="address.formattedAddress"]');
        this.remoteIfEmpty = remoteIfEmpty;
        this.synchronizedModuleIds = synchronizedModuleIds;
        this.displaySuggestions = displaySuggestions;

        // let's be nice with dynamic templates (JSP, JS,...) and don't force them to conditionally include this module
        if (this.queryInput === null) {
            return;
        }

        this._createAddressFromInputs = this._createAddressFromInputs.bind(this);
        this._createAutocomplete = this._createAutocomplete.bind(this);
        this._initEventListeners = this._initEventListeners.bind(this);
        this._registerAddressInputs = this._registerAddressInputs.bind(this);
        this._synchronizeAutocomplete = this._synchronizeAutocomplete.bind(this);
        this._updateAddress = this._updateAddress.bind(this);

        this.addressInputs = this._registerAddressInputs();
        this.address = defaultValue || this._createAddressFromInputs();
        this.autocomplete = this._createAutocomplete();

        this._updateAddress(this.address);
        this._initEventListeners();
    }

    _createAutocomplete() {
        const suggestions = this.displaySuggestions
            ? defaultCitiesSuggestions[`${this.languageCode}_${this.countryCode}`] || defaultCitiesSuggestions[this.countryCode] || []
            : [];

        const focusPoint = window.GEOLOCATION_FOCUS_POINT || (suggestions && suggestions.length > 0 ? suggestions[0].loc : null);

        return new LocationAutocomplete({
            countryCode: this.countryCode,
            defaultCitySuggestions: suggestions,
            defaultValue: this.address,
            focusPoint,
            input: this.queryInput,
            languageCode: this.languageCode,
            layer: 'LOCALITY',
            remoteIfEmpty: this.remoteIfEmpty,
            remoteCountryOptionLabel: this.allowRemote ? Hop.message(`search.location.remote.${this.countryCode}`) : null,
            remoteEuropaLabel: this.allowRemoteEuropa ? Hop.message(`search.location.remote.europa`) : null,
            onSelect: (selected) => {
                this._updateAddress(selected);
                Hop.events.trigger(LOCATION_CHANGED, selected, this.id);
            },
        });
    }

    _updateAddress(address) {
        this.address = address;
        this._updateInputValue(this.queryInput, (this.address && this.address.formattedAddress) || '');

        Object.entries(FIELDS_MAPPING).forEach(([key, mapping]) => {
            const input = this.addressInputs[key];
            if (!input) {
                return;
            }
            if (!mapping) {
                this._updateInputValue(input, '');
                input.disabled = true;
            }
            const value = _get(this.address, mapping);
            if (value == null) {
                this._updateInputValue(input, '');
                input.disabled = true;
            } else {
                this._updateInputValue(input, value);
                input.disabled = false;
            }
        });
    }

    _updateInputValue(input, value) {
        input.value = value;
    }

    _registerAddressInputs() {
        const result = {};
        Object.keys(FIELDS_MAPPING).forEach((key) => {
            result[key] =
                this.form.querySelector(`input[name="${key}"]`) ||
                this.form.querySelector(`input[name="address.${key}"]`) ||
                this.form.querySelector(`input[name="address.loc.${key}"]`);
        });
        return result;
    }

    _createAddressFromInputs() {
        const result = {
            formattedAddress: this.queryInput.value,
        };
        Object.entries(FIELDS_MAPPING).forEach(([key, mapping]) => {
            const input = this.addressInputs[key];
            if (!input || !mapping || input.disabled || !input.value) {
                return;
            }
            _set(result, mapping, input.value);
        });
        if (Object.values(result).find((value) => !!value)) {
            // if all the values are empty let's consider the address is blank
            return result;
        }
        return null;
    }

    _synchronizeAutocomplete(address, id) {
        if (!this.synchronizedModuleIds.includes(id)) {
            return;
        }

        this._updateAddress(address);
        this.autocomplete.setSelectedValue(address);
    }

    _initEventListeners() {
        Hop.events.on(LOCATION_CHANGED, this._synchronizeAutocomplete);
    }

    destroy() {
        Hop.events.off(LOCATION_CHANGED, this._synchronizeAutocomplete);
        this.autocomplete.destroy();
    }
}

const FIELDS_MAPPING = {
    city: 'city',
    countryCode: 'countryCode',
    country: 'country',
    administrativeAreaLevel1: 'administrativeAreaLevel1',
    administrativeAreaLevel1Code: 'administrativeAreaLevel1Code',
    administrativeAreaLevel2: 'administrativeAreaLevel2',
    administrativeAreaLevel2Code: 'administrativeAreaLevel2Code',
    administrativeAreaLevel3: null,
    administrativeAreaLevel3Code: null,
    administrativeAreaLevel4: null,
    administrativeAreaLevel4Code: null,
    lon: 'loc.lon',
    lat: 'loc.lat',
    remoteAllowed: 'remoteAllowed',
    remoteEuropa: 'remoteEuropa',
};
